/**
 * @file LearningPage.jsx
 * @description Serves as the primary landing page for educational content (DSA and System Design).
 *              Displays curated features and dynamic CTA buttons depending on the user's authentication state.
 * @module components/LearningPage
 */
import React, { useEffect } from 'react';
import {
    FiCode,
    FiActivity,
    FiBarChart2,
    FiLayers,
    FiDatabase,
    FiTool
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import Button from './ui/Button';

/* ══════════════════════════════════════════════════════════════
 * DATA CONSTANTS
 * ══════════════════════════════════════════════════════════════ */

const DSA_FEATURES = [
    {
        icon: FiCode,
        title: 'Structured Practice',
        description: 'Progress through carefully curated problems from basic to advanced levels.',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50'
    },
    {
        icon: FiActivity,
        title: 'Real-time Feedback',
        description: 'Get instant results with detailed explanations and optimization tips.',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    {
        icon: FiBarChart2,
        title: 'Track Progress',
        description: 'Monitor your growth with comprehensive analytics and achievements.',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    }
];

const SYSTEM_DESIGN_FEATURES = [
    {
        icon: FiLayers,
        title: 'System Architecture',
        description: 'Learn to design scalable, distributed systems with real-world examples.',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50'
    },
    {
        icon: FiDatabase,
        title: 'Database Design',
        description: 'Master data modeling, optimization, and distributed database concepts.',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    {
        icon: FiTool,
        title: 'Industry Tools',
        description: 'Understand modern technologies and architectural patterns used in production.',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    }
];

/* ══════════════════════════════════════════════════════════════
 * SUB-COMPONENTS
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component DSASection
 * @description Renders the 'Master DSA Problems' segment of the Learning page.
 *              Displays a hero value proposition, dynamic call-to-action buttons based on auth state,
 *              and a list of benefits/features (hidden on small mobile screens).
 * 
 * @param {Object} props
 * @param {Function} props.navigate - React Router navigation function
 * @param {boolean} props.isAuthenticated - Determines if you show 'Sign In' or 'Start Coding'
 * @param {Function} props.signInWithGoogle - Authentication handler
 *
 * @returns {JSX.Element} The rendered DSA section
 */
const DSASection = ({ navigate, isAuthenticated, signInWithGoogle }) => (
    <section className="py-16 sm:py-24 px-8 sm:px-12 lg:px-16 bg-accent border-b-4 border-emerald-600 relative overflow-hidden">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-accent pointer-events-none" />
        
        <div className="relative z-10 max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Heading + Description + Button */}
            <div className="space-y-8 order-1 flex flex-col">
                <div className="space-y-6 text-left">
                    <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-foreground">
                        Master{" "}
                        <span className="block bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                            DSA Problems
                        </span>
                    </h2>
                    <p className="text-md md:text-lg text-muted-foreground leading-relaxed text-left max-w-xl">
                        Build strong programming foundations with our comprehensive
                        collection of <strong className="text-foreground">Data Structures and Algorithms</strong> problems — from basic
                        concepts to advanced techniques.
                    </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                    {isAuthenticated ? (
                        <Button size="lg" onClick={() => navigate('/dsa-problems')} className="w-full sm:w-auto">
                            <FiCode className="w-5 h-5 mr-2" />
                            Start Coding
                        </Button>
                    ) : (
                        <>
                            <Button size="lg" onClick={() => navigate('/dsa-problems')} className="w-full sm:w-auto">
                                <FiCode className="w-5 h-5 mr-2" />
                                Start Coding
                            </Button>
                            <Button size="lg" variant="outline" onClick={signInWithGoogle}>
                                <FcGoogle className="w-5 h-5 mr-2" />
                                Sign in with Google
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Right: Features List */}
            <div className="space-y-6 order-2 bg-card p-6 sm:p-8 rounded border-l-4 border-emerald-600 shadow-sm hidden md:block">
                <h3 className="text-xl font-bold text-foreground mb-6 text-left">Why Practice Here?</h3>
                <div className="space-y-6">
                    {DSA_FEATURES.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-1">
                            <div className={`w-12 h-12 flex-shrink-0 flex items-start justify-start mt-1`}>
                                <feature.icon className={`w-6 h-6 text-emerald-600`} />
                            </div>
                            <div className="text-left flex-1">
                                <h4 className="text-base font-semibold text-emerald-600 mb-1">{feature.title}</h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

/**
 * @component SystemDesignSection
 * @description Renders the 'Learn System Designs' segment of the Learning page.
 *              Showcases system architecture propositions and provides direct CTA links.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - React Router navigation function
 * @param {boolean} props.isAuthenticated - Determines if you show 'Sign In' or 'Start Learning'
 * @param {Function} props.signInWithGoogle - Authentication handler
 *
 * @returns {JSX.Element} The rendered System Design section
 */
const SystemDesignSection = ({ navigate, isAuthenticated, signInWithGoogle }) => (
    <section className="py-16 sm:py-24 px-8 sm:px-12 lg:px-16 bg-accent">
        <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left: Features List */}
            <div className="space-y-6 order-2 lg:order-1 bg-card p-6 sm:p-8 rounded border-l-4 border-emerald-600 hidden md:block">
                <h3 className="text-xl font-bold text-foreground mb-6 text-left">What You'll Learn</h3>
                <div className="space-y-6">
                    {SYSTEM_DESIGN_FEATURES.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-1">
                            <div className={`w-12 h-12 flex-shrink-0 flex items-start justify-start mt-1`}>
                                <feature.icon className={`w-6 h-6 text-emerald-600`} />
                            </div>
                            <div className="text-left flex-1">
                                <h4 className="text-base font-semibold text-emerald-600 mb-1">{feature.title}</h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: Heading + Description + Button */}
            <div className="space-y-8 order-1 lg:order-2 flex flex-col">
                <div className="space-y-6 text-left">
                    <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-foreground">
                        Learn{" "}
                        <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent pb-2">
                            System Designs
                        </span>
                    </h2>
                    <p className="text-md md:text-lg text-muted-foreground leading-relaxed text-left max-w-xl">
                        Understand how to architect large-scale distributed systems. Learn the patterns
                        and principles used by top tech companies to build robust, scalable applications.
                    </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                    {isAuthenticated ? (
                        <Button size="lg" onClick={() => navigate('/system-design')} className="w-full sm:w-auto">
                            <FiLayers className="w-5 h-5 mr-2" />
                            Start Learning
                        </Button>
                    ) : (
                        <>
                            <Button size="lg" onClick={() => navigate('/system-design')} className="w-full sm:w-auto">
                                <FiLayers className="w-5 h-5 mr-2" />
                                Start Learning
                            </Button>
                            <Button size="lg" variant="outline" onClick={signInWithGoogle}>
                                <FcGoogle className="w-5 h-5 mr-2" />
                                Sign in with Google
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    </section>
);

/* ══════════════════════════════════════════════════════════════
 * MAIN PAGE COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component LearningPage
 * @description Main container for the Learning platform funnel.
 *              Combines the global Navbar/Footer with the DSA and System Design sections.
 *              Overrides default Navbar links to display 'Home' instead of 'Learning'.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - React Router navigation handler passed down from App router
 *
 * @returns {JSX.Element} The full Learning Page view
 */
const LearningPage = ({ navigate }) => {
    const { isAuthenticated, signInWithGoogle } = useAuth();

    // Set browser tab title on mount
    useEffect(() => { document.title = 'SimpleCode | Learning'; }, []);

    // Define custom navigation links for the learning page specifically
    const customNavLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={customNavLinks} />

            {/* Main Content Area */}
            <main className="flex-1 pt-20"> {/* PT-20 to clear fixed navbar */}
                <DSASection 
                    navigate={navigate} 
                    isAuthenticated={isAuthenticated} 
                    signInWithGoogle={signInWithGoogle} 
                />
                
                <SystemDesignSection 
                    navigate={navigate} 
                    isAuthenticated={isAuthenticated} 
                    signInWithGoogle={signInWithGoogle} 
                />
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default LearningPage;